import type { UserNotification } from '@/types/notifications';

export const COMMENT_NOTIFICATION_TYPES = [
  'tagged_in_comment',
  'added_a_comment',
  'tagged_in_group_chat',
  'added_a_group_chat_message',
  'group_chat_message',
] as const;

export type CommentNotificationType = (typeof COMMENT_NOTIFICATION_TYPES)[number];

/** Notification types that represent unread group chat activity (messages + mentions). */
export const GROUP_CHAT_NOTIFICATION_TYPES = [
  'added_a_group_chat_message',
  'tagged_in_group_chat',
  'group_chat_message',
] as const;

export type GroupChatNotificationType = (typeof GROUP_CHAT_NOTIFICATION_TYPES)[number];

/** Notification types that change group/folder/task unread badges (not group chat). */
export const LIST_ITEM_NOTIFICATION_TYPES = [
  'list_item_added',
  'list_item_deleted',
  'list_item_updated',
  'list_item_moved',
  'list_item_renamed',
  'list_item_restored',
] as const;

export type ListItemNotificationType = (typeof LIST_ITEM_NOTIFICATION_TYPES)[number];

export function normalizeNotificationType(notificationType: string): string {
  return notificationType.trim().toLowerCase();
}

export function isCommentNotificationType(
  notificationType: string
): notificationType is CommentNotificationType {
  const normalized = normalizeNotificationType(notificationType);
  return COMMENT_NOTIFICATION_TYPES.includes(normalized as CommentNotificationType);
}

export function filterCommentNotifications(
  notifications: UserNotification[]
): UserNotification[] {
  return notifications.filter((notification) =>
    isCommentNotificationType(notification.notificationType)
  );
}

/**
 * Whether a notification type represents group chat activity (new message or @mention).
 */
export function isGroupChatNotificationType(
  notificationType: string
): notificationType is GroupChatNotificationType {
  const normalized = normalizeNotificationType(notificationType);
  return GROUP_CHAT_NOTIFICATION_TYPES.includes(normalized as GroupChatNotificationType);
}

/**
 * Whether a notification type represents list-item activity (tasks, folders, etc.).
 */
export function isListItemNotificationType(
  notificationType: string,
): notificationType is ListItemNotificationType {
  const normalized = normalizeNotificationType(notificationType);
  return LIST_ITEM_NOTIFICATION_TYPES.includes(normalized as ListItemNotificationType);
}

/**
 * Notifications that should refresh {@link GroupDetails.totalUnreadCount} and folder item badges.
 * Excludes group-chat-only types, which use the live inbox poll for chat counts.
 */
export function isGroupUnreadCountNotification(notification: UserNotification): boolean {
  if (isGroupChatNotificationType(notification.notificationType)) {
    return false;
  }

  return (
    isListItemNotificationType(notification.notificationType) ||
    isCommentNotificationType(notification.notificationType)
  );
}

/**
 * Whether a notification has a usable group id for group-chat badge routing.
 */
export function isValidNotificationGroupId(groupId: unknown): groupId is number {
  if (groupId == null) {
    return false;
  }

  const normalized = Number(groupId);
  return Number.isFinite(normalized) && normalized > 0;
}

/**
 * Whether a notification record is a group chat notification eligible for badges.
 * When sourced from `status=unread`, trust the query filter over per-item `isRead`.
 */
export function isGroupChatNotification(notification: UserNotification): boolean {
  return (
    isGroupChatNotificationType(notification.notificationType) &&
    isValidNotificationGroupId(notification.groupId)
  );
}

/**
 * Filters notifications to group-chat types only.
 */
export function filterGroupChatNotifications(
  notifications: UserNotification[]
): UserNotification[] {
  return notifications.filter((notification) =>
    isGroupChatNotificationType(notification.notificationType)
  );
}

/**
 * Builds per-group unread counts from group-chat notifications.
 * Keys match `GroupDetails.id` (string) for reliable sidebar lookups.
 */
export function buildUnreadGroupChatCountByGroupId(
  notifications: UserNotification[]
): Record<string, number> {
  const countsByGroupId: Record<string, number> = {};

  for (const notification of filterGroupChatNotifications(notifications)) {
    if (!isValidNotificationGroupId(notification.groupId)) {
      continue;
    }

    const groupId = String(notification.groupId);
    countsByGroupId[groupId] = (countsByGroupId[groupId] ?? 0) + 1;
  }

  return countsByGroupId;
}

/**
 * Prefer comment notifications; fall back to the latest unread inbox item.
 */
export function pickAlertNotification(
  notifications: UserNotification[]
): UserNotification | null {
  if (notifications.length === 0) {
    return null;
  }

  const commentNotifications = filterCommentNotifications(notifications);
  const pool = commentNotifications.length > 0 ? commentNotifications : notifications;

  return [...pool].sort(
    (left, right) =>
      (right.notifiedAt ?? right.createdAt) - (left.notifiedAt ?? left.createdAt)
  )[0];
}

/**
 * Extracts the list item name from the API notification body, e.g. Folder "My Folder".
 */
export function extractFolderNameFromNotificationBody(body: string): string {
  const match = body.match(/on a (?:Folder|Task|Text|Media|Group) "([^"]+)"/i);
  return match?.[1] ?? 'group chat';
}
